/**
 * Shared core for the two assignee-picker shells (CU-P4):
 *   - molecules/AssigneePicker.tsx        — full inline sectioned list
 *   - design-system/patterns/task-detail-pickers.tsx PeekAssigneePicker
 *     — Popover-triggered chips + add
 *
 * Both need the same live-search data source and the same section
 * grouping/order; before this module they each reimplemented both
 * independently. This is the "one canonical" for that shared logic — see
 * docs/features/assignee-picker-consolidation.md for why the two render
 * shells themselves stay separate rather than being merged into one
 * component.
 */
import { useEffect, useState } from 'react'
import { assignablePeople as assignablePeopleApi, type AssignablePerson } from '@/lib/api'

export function assignablePeopleRequestParams(
  projectId: number | string | undefined,
  query: string,
): { projectId?: number | string; q?: string } | undefined {
  const q = query.trim()
  const params = {
    ...(projectId ? { projectId } : {}),
    ...(q ? { q } : {}),
  }
  return Object.keys(params).length ? params : undefined
}

/**
 * Canonical live data source for every picker state. With `projectId`, the
 * unsearched view is the project roster and search reaches the company
 * directory. Without `projectId`, both the default view and search use the
 * full company assignable-person directory. Project selection may annotate
 * access/relevance, but must never change a person's identity or row shape.
 */
export function useLiveAssignablePeople(
  projectId: number | string | undefined,
  query: string,
): { people: AssignablePerson[] | null; accessIds: number[] | undefined } {
  const [people, setPeople] = useState<AssignablePerson[] | null>(null)
  const [accessIds, setAccessIds] = useState<number[] | undefined>(undefined)

  useEffect(() => {
    const q = query.trim()
    let cancelled = false
    const handle = window.setTimeout(async () => {
      try {
        const res = await assignablePeopleApi.list(assignablePeopleRequestParams(projectId, q))
        if (cancelled) return
        if (Array.isArray(res)) { setPeople(res); setAccessIds(undefined) }
        else { setPeople(res.people); setAccessIds(res.project_access_ids) }
      } catch {
        if (!cancelled) { setPeople([]); setAccessIds(undefined) }
      }
    }, q ? 300 : 0) // debounce searches only; the initial directory/roster load fires immediately
    return () => { cancelled = true; window.clearTimeout(handle) }
  }, [projectId, query])

  return { people, accessIds }
}

// Fixed section order + labels (spec). Any person_type not listed (external,
// other, legacy) falls into the trailing 'other' bucket so nothing is dropped.
export const ASSIGNEE_SECTIONS: { key: string; label: string }[] = [
  { key: 'employee', label: 'Employees' },
  { key: 'collaborator', label: 'Collaborators' },
  { key: 'subcontractor', label: 'Subcontractors' },
  { key: 'supplier', label: 'Suppliers' },
  { key: 'client', label: 'Clients' },
  { key: 'other', label: 'Other' },
]
const ASSIGNEE_SECTION_KEYS = new Set(ASSIGNEE_SECTIONS.map(s => s.key))

/** Canonical section for a raw person_type/role string (case-insensitive). */
export function sectionForPersonType(personType: string | undefined | null): string {
  const t = (personType || '').toLowerCase()
  if (t === '' || t === 'user' || t === 'internal') return 'employee' // login users w/o a contact person_type
  if (t === 'vendor') return 'supplier' // legacy alias
  if (t === 'architect') return 'collaborator'
  if (t === 'sub') return 'subcontractor'
  if (t === 'customer') return 'client'
  return ASSIGNEE_SECTION_KEYS.has(t) ? t : 'other'
}

const SECTION_SINGULAR_LABEL: Record<string, string> = {
  employee: 'Employee',
  collaborator: 'Collaborator',
  subcontractor: 'Subcontractor',
  supplier: 'Supplier',
  client: 'Client',
  other: 'Contact',
}

/** Human-readable singular label for a raw person_type/role string (SCHED-W4
 *  §12: "secondary line shows a human role/trade, not the raw person_type
 *  enum" — #2430 AC6). */
export function personTypeLabel(personType: string | undefined | null): string {
  return SECTION_SINGULAR_LABEL[sectionForPersonType(personType)]
}

/** Group a flat person list into the canonical section buckets, preserving
 *  ASSIGNEE_SECTIONS order. Sections with zero matches are omitted. */
export function groupBySection<T extends { person_type?: string; role?: string }>(
  people: T[],
): { key: string; label: string; items: T[] }[] {
  const buckets: Record<string, T[]> = {}
  for (const p of people) {
    const sec = sectionForPersonType(p.person_type ?? p.role)
    ;(buckets[sec] ||= []).push(p)
  }
  return ASSIGNEE_SECTIONS
    .map(s => ({ ...s, items: buckets[s.key] || [] }))
    .filter(s => s.items.length > 0)
}
